/**
 * Filter algebra — build, validate, describe, share and evaluate a `FilterSet`.
 *
 * Two rules drive the design:
 *
 *  1. **Every view is a URL.** A filtered list must survive a copy-paste into
 *     Slack and the browser's back button, so the whole list state round-trips
 *     through `URLSearchParams` losslessly.
 *  2. **The client can evaluate a filter too.** `matchesInquiry` lets realtime
 *     decide whether a freshly created record belongs in the list you are
 *     looking at, instead of blindly refetching every open query.
 */

import {
  BAND_META,
  FILTER_FIELDS_BY_NAME,
  INQUIRY_TYPE_META,
  MULTI_VALUE_OPERATORS,
  OPERATOR_LABELS,
  PRIORITY_META,
  REGION_META,
  SOURCE_META,
  STATUS_META,
  VALUELESS_OPERATORS,
  stageMeta,
  type FilterFieldMeta,
} from '@/lib/constants';
import { formatFilterValue, type FilterValueKind } from '@/lib/format';
import type {
  FilterCondition,
  FilterOperator,
  FilterSet,
  ID,
  Inquiry,
  ListQuery,
  SortSpec,
} from '@shared/types';

/* ────────────────────────────────────────────────────────────────────────────
   Construction
   ──────────────────────────────────────────────────────────────────────────── */

const NO_CONDITIONS: FilterCondition[] = [];
/** Shared, frozen "no filters" value — safe to use as a default prop. */
export const EMPTY_FILTER_SET: FilterSet = { combinator: 'and', conditions: NO_CONDITIONS };
Object.freeze(NO_CONDITIONS);
Object.freeze(EMPTY_FILTER_SET);

export function emptyFilterSet(): FilterSet {
  return { combinator: 'and', conditions: [] };
}

let conditionSeq = 0;
function nextConditionId(): string {
  conditionSeq += 1;
  return `fc_${Date.now().toString(36)}_${conditionSeq.toString(36)}`;
}

/** The operator a field should open with — the one people mean 90% of the time. */
export function defaultOperator(field: string): FilterOperator {
  const meta = FILTER_FIELDS_BY_NAME[field];
  return meta?.operators[0] ?? 'is';
}

function defaultValue(operator: FilterOperator): FilterCondition['value'] {
  if (VALUELESS_OPERATORS.includes(operator)) return null;
  if (MULTI_VALUE_OPERATORS.includes(operator)) return [];
  return null;
}

export function createCondition(
  field: string,
  operator: FilterOperator = defaultOperator(field),
  value: FilterCondition['value'] = defaultValue(operator),
): FilterCondition {
  return { id: nextConditionId(), field, operator, value };
}

export function addCondition(set: FilterSet, condition: FilterCondition): FilterSet {
  return { ...set, conditions: [...set.conditions, condition] };
}

/**
 * Patch one condition. Changing the *field* resets the operator and value —
 * carrying `contains 'foo'` over to a date field produces a silent no-op query.
 */
export function updateCondition(
  set: FilterSet,
  id: string,
  patch: Partial<Omit<FilterCondition, 'id'>>,
): FilterSet {
  return {
    ...set,
    conditions: set.conditions.map((c) => {
      if (c.id !== id) return c;
      if (patch.field && patch.field !== c.field) {
        const operator = patch.operator ?? defaultOperator(patch.field);
        return {
          id: c.id,
          field: patch.field,
          operator,
          value: patch.value ?? defaultValue(operator),
        };
      }
      if (patch.operator && patch.operator !== c.operator) {
        const next = { ...c, ...patch, operator: patch.operator };
        if (patch.value === undefined) next.value = coerceForOperator(c.value, patch.operator);
        return next;
      }
      return { ...c, ...patch };
    }),
  };
}

/** Keep as much of the user's typing as the new operator can hold. */
function coerceForOperator(
  value: FilterCondition['value'],
  operator: FilterOperator,
): FilterCondition['value'] {
  if (VALUELESS_OPERATORS.includes(operator)) return null;
  const wantsList = MULTI_VALUE_OPERATORS.includes(operator);
  const isList = Array.isArray(value);
  if (wantsList && !isList) return value === null || value === '' ? [] : [value as string | number];
  if (!wantsList && isList) return (value as Array<string | number>)[0] ?? null;
  return value;
}

export function removeCondition(set: FilterSet, id: string): FilterSet {
  return { ...set, conditions: set.conditions.filter((c) => c.id !== id) };
}

export function setCombinator(set: FilterSet, combinator: FilterSet['combinator']): FilterSet {
  return { ...set, combinator };
}

/** Replace (or add) the single condition on `field` — how quick chips behave. */
export function upsertFieldCondition(
  set: FilterSet,
  field: string,
  operator: FilterOperator,
  value: FilterCondition['value'],
): FilterSet {
  const existing = set.conditions.find((c) => c.field === field);
  if (!existing) return addCondition(set, createCondition(field, operator, value));
  return updateCondition(set, existing.id, { operator, value });
}

export function removeFieldConditions(set: FilterSet, field: string): FilterSet {
  return { ...set, conditions: set.conditions.filter((c) => c.field !== field) };
}

export function getFieldCondition(set: FilterSet, field: string): FilterCondition | undefined {
  return set.conditions.find((c) => c.field === field);
}

export function hasFieldCondition(set: FilterSet, field: string): boolean {
  return set.conditions.some((c) => c.field === field);
}

/* ────────────────────────────────────────────────────────────────────────────
   Validity
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * A condition is complete when it carries a value — i.e. when it is no longer
 * half-typed.
 *
 * **This is the only reason a condition may be dropped on the way to the wire,
 * and it is a reason the user can see**: a chip with an empty value slot is
 * visibly unfinished. It used to also drop anything whose field or operator was
 * missing from `FILTER_FIELDS`, which is not visible at all — the chip rendered
 * complete, with a field name, an operator and a formatted value, while
 * `sanitiseFilterSet` deleted it from the request. That is how "Won this
 * quarter" showed `Closed ≥ Jun 30, 2026` and asked for every won inquiry ever.
 *
 * Vocabulary is now enforced where it can be answered: `validateFilterSet` in
 * the builder (where the field came from a list, so it cannot be wrong), and
 * the API, which 422s an unknown field by name. A filter the server refuses is
 * an error on screen. A filter it never heard about is a lie.
 */
export function isConditionComplete(condition: FilterCondition): boolean {
  const meta = FILTER_FIELDS_BY_NAME[condition.field];
  if (VALUELESS_OPERATORS.includes(condition.operator)) return true;

  const { value, operator } = condition;
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) {
    if (value.length === 0) return false;
    if (operator === 'between' && value.length !== 2) return false;
    return true;
  }
  if (meta?.type === 'number' && Number.isNaN(Number(value))) return false;
  return true;
}

export interface FilterValidation {
  valid: boolean;
  /** Keyed by condition id, ready to hang under the offending row. */
  errors: Record<string, string>;
}

export function validateFilterSet(set: FilterSet): FilterValidation {
  const errors: Record<string, string> = {};
  for (const condition of set.conditions) {
    const meta = FILTER_FIELDS_BY_NAME[condition.field];
    if (!meta) {
      errors[condition.id] = `Unknown field "${condition.field}".`;
      continue;
    }
    if (!meta.operators.includes(condition.operator)) {
      errors[condition.id] = `${meta.label} does not support "${OPERATOR_LABELS[condition.operator]}".`;
      continue;
    }
    if (VALUELESS_OPERATORS.includes(condition.operator)) continue;
    if (
      condition.value === null ||
      condition.value === undefined ||
      condition.value === '' ||
      (Array.isArray(condition.value) && condition.value.length === 0)
    ) {
      errors[condition.id] = 'Pick a value.';
      continue;
    }
    if (condition.operator === 'between') {
      const value = condition.value;
      if (!Array.isArray(value) || value.length !== 2) {
        errors[condition.id] = 'Between needs a start and an end.';
        continue;
      }
      if (meta.type === 'number' && Number(value[0]) > Number(value[1])) {
        errors[condition.id] = 'The first value must be lower than the second.';
        continue;
      }
    }
    if (meta.type === 'number' && !Array.isArray(condition.value)) {
      const n = Number(condition.value);
      if (Number.isNaN(n)) errors[condition.id] = 'Enter a number.';
      else if (meta.min !== undefined && n < meta.min) errors[condition.id] = `Minimum is ${meta.min}.`;
      else if (meta.max !== undefined && n > meta.max) errors[condition.id] = `Maximum is ${meta.max}.`;
    }
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

/** Drop half-built conditions before a set goes near the network or a cache key. */
export function sanitiseFilterSet(set: FilterSet | undefined): FilterSet {
  if (!set) return emptyFilterSet();
  return { combinator: set.combinator, conditions: set.conditions.filter(isConditionComplete) };
}

export function isEmptyFilterSet(set: FilterSet | undefined): boolean {
  return !set || set.conditions.filter(isConditionComplete).length === 0;
}

export function countActiveConditions(set: FilterSet | undefined): number {
  return set ? set.conditions.filter(isConditionComplete).length : 0;
}

/**
 * A view's stored filters plus whatever the user typed on top. Overrides win
 * per field, so clicking "Stage: Won" inside a "Hot leads" view swaps that
 * view's stage clause instead of contradicting it.
 */
export function mergeFilterSets(base: FilterSet | undefined, override: FilterSet | undefined): FilterSet {
  const left = sanitiseFilterSet(base);
  const right = sanitiseFilterSet(override);
  if (right.conditions.length === 0) return left;
  if (left.conditions.length === 0) return right;

  const overriddenFields = new Set(right.conditions.map((c) => c.field));
  return {
    combinator: right.combinator,
    conditions: [
      ...left.conditions.filter((c) => !overriddenFields.has(c.field)),
      ...right.conditions,
    ],
  };
}

/** Structural equality — cheap enough to run on every render, exact enough to trust. */
export function filterSetsEqual(a: FilterSet | undefined, b: FilterSet | undefined): boolean {
  return serialiseForKey(a) === serialiseForKey(b);
}

/** Condition ids are ephemeral; a cache key must not change when one is regenerated. */
export function serialiseForKey(set: FilterSet | undefined): string {
  const clean = sanitiseFilterSet(set);
  if (clean.conditions.length === 0) return '';
  const conditions = clean.conditions
    .map((c) => `${c.field}|${c.operator}|${JSON.stringify(normaliseValue(c.value, c.operator))}`)
    .sort();
  return `${clean.combinator}:${conditions.join(';')}`;
}

/** Set membership is unordered; `between` bounds are not. */
function normaliseValue(
  value: FilterCondition['value'],
  operator: FilterOperator,
): FilterCondition['value'] {
  if (operator !== 'between' && Array.isArray(value)) {
    return [...value].sort((a, b) => String(a).localeCompare(String(b)));
  }
  return value;
}

/* ────────────────────────────────────────────────────────────────────────────
   Human-readable summaries
   ──────────────────────────────────────────────────────────────────────────── */

export interface SummaryContext {
  /**
   * Resolves an opaque id to a name — owner ids to people, tag ids to tags.
   * Returning `undefined` falls back to the built-in enum labels.
   */
  resolveValue?: (field: string, value: string | number) => string | undefined;
  /** Names listed before collapsing to `+N`. */
  maxListed?: number;
}

const ENUM_LABELS: Record<string, (value: string) => string | undefined> = {
  stageKey: (v) => stageMeta(v).label,
  /* One stage vocabulary: "how far it ever got" prints the same word the board
     column, the funnel rung and the `Stage` chip print. */
  furthestStageKey: (v) => stageMeta(v).label,
  status: (v) => STATUS_META[v as keyof typeof STATUS_META]?.label,
  priority: (v) => PRIORITY_META[v as keyof typeof PRIORITY_META]?.label,
  source: (v) => SOURCE_META[v as keyof typeof SOURCE_META]?.label,
  region: (v) => REGION_META[v as keyof typeof REGION_META]?.label,
  type: (v) => INQUIRY_TYPE_META[v as keyof typeof INQUIRY_TYPE_META]?.short,
  /* `BAND_META`, not a capitalise-the-key trick: the band table is where a
     band's name lives, and it is the name the score pill and the record's
     breakdown already print. */
  scoreBand: (v) => BAND_META[v as keyof typeof BAND_META]?.label,
};

function valueResolver(field: string, ctx: SummaryContext) {
  return (value: string | number): string | undefined => {
    const custom = ctx.resolveValue?.(field, value);
    if (custom !== undefined) return custom;
    return ENUM_LABELS[field]?.(String(value));
  };
}

export interface ConditionDescription {
  field: string;
  operator: string;
  value: string;
  meta: FilterFieldMeta | undefined;
}

/** The three parts of a filter chip, already formatted. */
export function describeCondition(
  condition: FilterCondition,
  ctx: SummaryContext = {},
): ConditionDescription {
  const meta = FILTER_FIELDS_BY_NAME[condition.field];
  return {
    field: meta?.label ?? condition.field,
    operator: OPERATOR_LABELS[condition.operator] ?? condition.operator,
    value: formatFilterValue(condition.value, {
      kind: (meta?.valueKind as FilterValueKind | undefined) ?? 'text',
      operator: condition.operator,
      resolve: valueResolver(condition.field, ctx),
      maxListed: ctx.maxListed ?? 2,
    }),
    meta,
  };
}

/** `Stage is Qualifying, Score > 70` — the saved-view subtitle and export caption. */
export function summariseFilterSet(set: FilterSet | undefined, ctx: SummaryContext = {}): string {
  const clean = sanitiseFilterSet(set);
  if (clean.conditions.length === 0) return 'No filters';

  const separator = clean.combinator === 'or' ? ' or ' : ', ';
  return clean.conditions
    .map((condition) => {
      const d = describeCondition(condition, ctx);
      if (VALUELESS_OPERATORS.includes(condition.operator)) return `${d.field} ${d.operator}`;
      return `${d.field} ${d.operator} ${d.value}`;
    })
    .join(separator);
}

/* ────────────────────────────────────────────────────────────────────────────
   URL serialisation
   ──────────────────────────────────────────────────────────────────────────── */

/** Everything a list screen needs to reproduce itself from a pasted link. */
export interface ListState {
  q: string;
  filters: FilterSet;
  sort: SortSpec[];
  viewId: ID | null;
}

export const PARAM = {
  q: 'q',
  filters: 'f',
  combinator: 'fc',
  sort: 'sort',
  view: 'view',
} as const;

export function emptyListState(): ListState {
  return { q: '', filters: emptyFilterSet(), sort: [], viewId: null };
}

/**
 * `f=stageKey:in:qualifying,review;score:gt:70` — readable in the address bar,
 * and lossless because the value's type is recovered from `FILTER_FIELDS`.
 */
function encodeConditions(set: FilterSet): string {
  return sanitiseFilterSet(set)
    .conditions.map((c) => {
      const parts = [c.field, c.operator];
      if (!VALUELESS_OPERATORS.includes(c.operator)) {
        const raw = Array.isArray(c.value)
          ? c.value.map((v) => encodePart(String(v))).join(',')
          : encodePart(String(c.value));
        parts.push(raw);
      }
      return parts.join(':');
    })
    .join(';');
}

/** `;` `:` `,` are structural; percent-encode only those three. */
function encodePart(value: string): string {
  return value.replace(/%/g, '%25').replace(/;/g, '%3B').replace(/:/g, '%3A').replace(/,/g, '%2C');
}

function decodePart(value: string): string {
  return value
    .replace(/%3B/gi, ';')
    .replace(/%3A/gi, ':')
    .replace(/%2C/gi, ',')
    .replace(/%25/g, '%');
}

function decodeScalar(field: string, raw: string): string | number | boolean {
  const meta = FILTER_FIELDS_BY_NAME[field];
  const value = decodePart(raw);
  if (!meta) return value;
  if (meta.type === 'number') {
    const n = Number(value);
    return Number.isNaN(n) ? value : n;
  }
  if (meta.type === 'boolean') return value === 'true';
  return value;
}

export function decodeConditions(raw: string | null): FilterCondition[] {
  if (!raw) return [];
  const conditions: FilterCondition[] = [];

  for (const chunk of raw.split(';')) {
    if (!chunk) continue;
    const [field, operator, ...rest] = chunk.split(':');
    if (!field || !operator) continue;
    const op = operator as FilterOperator;

    let value: FilterCondition['value'] = null;
    if (!VALUELESS_OPERATORS.includes(op)) {
      const rawValue = rest.join(':');
      value = MULTI_VALUE_OPERATORS.includes(op)
        ? rawValue
            .split(',')
            .filter(Boolean)
            .map((v) => {
              const scalar = decodeScalar(field, v);
              return typeof scalar === 'boolean' ? String(scalar) : scalar;
            })
        : decodeScalar(field, rawValue);
    }
    conditions.push({ id: nextConditionId(), field, operator: op, value });
  }
  return conditions;
}

export function encodeSort(sort: SortSpec[]): string {
  return sort.map((s) => `${s.field}:${s.direction === 'asc' ? 'asc' : 'desc'}`).join(',');
}

export function decodeSort(raw: string | null): SortSpec[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((token) => {
      const [field, direction] = token.split(':');
      if (!field) return null;
      return { field, direction: direction === 'asc' ? 'asc' : 'desc' } satisfies SortSpec;
    })
    .filter((s): s is SortSpec => s !== null);
}

/**
 * Writes list state into search params. Unrelated params on `base` survive, so
 * this composes with whatever else a screen keeps in the URL.
 */
export function encodeListState(
  state: Partial<ListState>,
  base?: URLSearchParams | string,
): URLSearchParams {
  const params = new URLSearchParams(base ?? undefined);

  const q = state.q?.trim();
  if (q) params.set(PARAM.q, q);
  else params.delete(PARAM.q);

  const encoded = state.filters ? encodeConditions(state.filters) : '';
  if (encoded) params.set(PARAM.filters, encoded);
  else params.delete(PARAM.filters);

  if (state.filters?.combinator === 'or' && encoded) params.set(PARAM.combinator, 'or');
  else params.delete(PARAM.combinator);

  const sort = state.sort && state.sort.length > 0 ? encodeSort(state.sort) : '';
  if (sort) params.set(PARAM.sort, sort);
  else params.delete(PARAM.sort);

  if (state.viewId) params.set(PARAM.view, state.viewId);
  else params.delete(PARAM.view);

  return params;
}

export function decodeListState(params: URLSearchParams | string): ListState {
  const p = typeof params === 'string' ? new URLSearchParams(params) : params;
  return {
    q: p.get(PARAM.q) ?? '',
    filters: {
      combinator: p.get(PARAM.combinator) === 'or' ? 'or' : 'and',
      conditions: decodeConditions(p.get(PARAM.filters)),
    },
    sort: decodeSort(p.get(PARAM.sort)),
    viewId: p.get(PARAM.view),
  };
}

/** `?f=stageKey:is:won&sort=score:desc` — for `navigate()` and copy-link. */
export function listStateToSearch(state: Partial<ListState>): string {
  const qs = encodeListState(state).toString();
  return qs ? `?${qs}` : '';
}

/** The wire shape the API client wants. */
export function listStateToQuery(state: ListState, extra?: Partial<ListQuery>): ListQuery {
  const filters = sanitiseFilterSet(state.filters);
  return {
    q: state.q.trim() || undefined,
    filters: filters.conditions.length > 0 ? filters : undefined,
    sort: state.sort.length > 0 ? state.sort : undefined,
    viewId: state.viewId ?? undefined,
    ...extra,
  };
}

/* ────────────────────────────────────────────────────────────────────────────
   Client-side evaluation
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * Fields whose value is not a plain path on `Inquiry`.
 *
 * One entry per computed field in `FILTER_FIELDS`. A field the vocabulary
 * offers but this map cannot read silently evaluates to `undefined`, which
 * makes `matchesInquiry` answer "no" for every live row — so a realtime arrival
 * would never join the "SLA at risk" list it belongs in, and the toolbar's
 * count would be the only thing on screen that knew.
 */
const INQUIRY_ACCESSORS: Record<string, (inquiry: Inquiry) => unknown> = {
  tags: (i) => i.tags.map((t) => t.id),
  contactName: (i) => i.contact?.fullName ?? null,
  contactEmail: (i) => i.contact?.email ?? null,
  contactTitle: (i) => i.contact?.title ?? null,
  companyName: (i) => i.company?.name ?? null,
  companySize: (i) => i.company?.size ?? null,
  companySegment: (i) => i.company?.segment ?? null,
  ownerName: (i) => i.owner?.name ?? null,
  hasAttachments: (i) => (i.counts?.attachments ?? i.attachments?.length ?? 0) > 0,
  isUnassigned: (i) => i.ownerId === null,
  slaBreached: (i) =>
    i.firstResponseAt === null && i.slaDueAt !== null && Date.parse(i.slaDueAt) < Date.now(),

  utmSource: (i) => i.utm.source,
  utmMedium: (i) => i.utm.medium,
  utmCampaign: (i) => i.utm.campaign,
};

function readPath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, source);
}

export function inquiryFieldValue(inquiry: Inquiry, field: string): unknown {
  const accessor = INQUIRY_ACCESSORS[field];
  return accessor ? accessor(inquiry) : readPath(inquiry, field);
}

function toComparable(value: unknown): number | string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') {
    const asDate = Date.parse(value);
    if (!Number.isNaN(asDate) && /^\d{4}-\d{2}-\d{2}/.test(value)) return asDate;
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? value.toLowerCase() : asNumber;
  }
  return null;
}

function compare(a: unknown, b: unknown): number | null {
  const left = toComparable(a);
  const right = toComparable(b);
  if (left === null || right === null) return null;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right));
}

function evaluate(actual: unknown, condition: FilterCondition): boolean {
  const { operator, value } = condition;
  const list = Array.isArray(actual) ? actual.map(String) : null;

  switch (operator) {
    case 'is_empty':
      return actual === null || actual === undefined || actual === '' || list?.length === 0;
    case 'is_not_empty':
      return !(actual === null || actual === undefined || actual === '' || list?.length === 0);
    case 'is':
      if (list) return list.includes(String(value));
      if (typeof actual === 'boolean') return actual === (value === true || value === 'true');
      return String(actual ?? '') === String(value ?? '');
    case 'is_not':
      return !evaluate(actual, { ...condition, operator: 'is' });
    case 'in': {
      const wanted = (Array.isArray(value) ? value : [value]).map(String);
      if (list) return list.some((v) => wanted.includes(v));
      return wanted.includes(String(actual ?? ''));
    }
    case 'not_in':
      return !evaluate(actual, { ...condition, operator: 'in' });
    case 'contains':
      return String(actual ?? '')
        .toLowerCase()
        .includes(String(value ?? '').toLowerCase());
    case 'not_contains':
      return !evaluate(actual, { ...condition, operator: 'contains' });
    case 'gt': {
      const c = compare(actual, value);
      return c !== null && c > 0;
    }
    case 'gte': {
      const c = compare(actual, value);
      return c !== null && c >= 0;
    }
    case 'lt': {
      const c = compare(actual, value);
      return c !== null && c < 0;
    }
    case 'lte': {
      const c = compare(actual, value);
      return c !== null && c <= 0;
    }
    case 'between': {
      if (!Array.isArray(value) || value.length !== 2) return true;
      const low = compare(actual, value[0]);
      const high = compare(actual, value[1]);
      return low !== null && high !== null && low >= 0 && high <= 0;
    }
    default:
      return true;
  }
}

/**
 * Evaluate a filter set against an arbitrary record.
 *
 * This is a *best-effort mirror* of the server's SQL, used only to decide
 * whether a realtime row belongs in a list that is already on screen. It is
 * never a substitute for asking the server.
 */
export function matchesFilterSet(
  set: FilterSet | undefined,
  getValue: (field: string) => unknown,
): boolean {
  const clean = sanitiseFilterSet(set);
  if (clean.conditions.length === 0) return true;
  const results = clean.conditions.map((c) => evaluate(getValue(c.field), c));
  return clean.combinator === 'or' ? results.some(Boolean) : results.every(Boolean);
}

export function matchesInquiry(inquiry: Inquiry, set: FilterSet | undefined): boolean {
  return matchesFilterSet(set, (field) => inquiryFieldValue(inquiry, field));
}

/**
 * Fields whose answer lives only on the server, so no accessor can be written.
 *
 * `furthestStageKey` is derived from the inquiry's whole stage history in
 * `activities`, which a list row does not carry — `readPath` would return
 * `undefined`, `in` would evaluate false, and a realtime arrival would silently
 * fail to join a list it belongs in while the toolbar's count knew better. The
 * honest answer is "ask the server", which is what `false` here buys.
 */
const SERVER_ONLY_FIELDS = new Set<string>(['furthestStageKey']);

/** Fields this client cannot evaluate — realtime falls back to invalidation. */
export function isClientEvaluable(set: FilterSet | undefined): boolean {
  const clean = sanitiseFilterSet(set);
  return clean.conditions.every(
    (c) =>
      !SERVER_ONLY_FIELDS.has(c.field) &&
      (c.field in INQUIRY_ACCESSORS || FILTER_FIELDS_BY_NAME[c.field] !== undefined),
  );
}
